import * as React from "react"

import { cn } from "@/utils/utils"
import { FIELD, FIELD_FOCUS, fieldLook, type FieldLook } from "@/components/ui/recipes"

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Filled (a form's) or outlined (an editor panel's); left out, filled. */
  look?: FieldLook
  /** Its height follows its text (set by its owner), so it has no minimum of its own. */
  autoGrow?: boolean
  /**
   * Drawn under the text and lined up with it, in transparent text (a highlight on a span of it). Null while there is
   * nothing to draw keeps the same field, so the text area keeps its focus.
   */
  underlay?: React.ReactNode
}

/** Lureo's text area: the text field's look, several lines tall. */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, look, autoGrow = false, underlay, ...props }, ref) => {
    if (underlay === undefined) {
      return (
        <textarea
          className={cn(FIELD, fieldLook("md", look), FIELD_FOCUS.md, "flex px-3 py-2 text-lureo-14 leading-5", autoGrow ? "resize-none" : "min-h-[80px]", className)}
          ref={ref}
          {...props}
        />
      )
    }
    // The frame is the field; the layer under the text and the text inherit its padding box and type, so they line up
    return (
      <div className={cn(FIELD, fieldLook("md", look), "relative px-3 py-2 text-lureo-14 focus-within:ring-2 focus-within:ring-lureo-accent/70 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-lureo-disabled-opacity leading-5", !autoGrow && "min-h-[80px]", className)}>
        <div aria-hidden className="pointer-events-none absolute inset-x-3 inset-y-2 whitespace-pre-wrap break-words text-transparent">
          {underlay}
        </div>
        <textarea
          ref={ref}
          className="relative block w-full resize-none overflow-y-auto bg-transparent outline-hidden placeholder:text-lureo-ink-subtlest disabled:cursor-not-allowed"
          {...props}
        />
      </div>
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }
